import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Filter } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@akuna/ui";
import { SearchField } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AppointmentStatusBadge } from "@/components/patients/appointment-ui-badges";
import { formatCurrency } from "@/components/patients/appointment-ui-meta";
import type { FutureRow } from "@/hooks/useFutureRows";
import { cn } from "@/lib/utils";

export function FutureTabContent({
  pendingRows,
  upcomingRows,
  isLoading,
  showClinicHint,
  showPetColumn = false,
  showToolbar = true,
  pendingEmptyMessage,
  upcomingEmptyMessage,
  onVaccinationSelect,
  onPlanSelect,
  onAppointmentSelect,
  compact = false,
  hideSectionDescriptions = false,
  tableMaxHeightClassName,
}: {
  pendingRows: FutureRow[];
  upcomingRows: FutureRow[];
  isLoading: boolean;
  showClinicHint: boolean;
  showPetColumn?: boolean;
  showToolbar?: boolean;
  pendingEmptyMessage: string;
  upcomingEmptyMessage: string;
  onVaccinationSelect?: (
    vaccination: NonNullable<FutureRow["editableVaccination"]>,
    row: FutureRow,
  ) => void;
  onPlanSelect?: (
    plan: NonNullable<FutureRow["editablePlan"]>,
    row: FutureRow,
  ) => void;
  onAppointmentSelect?: (row: FutureRow) => void;
  compact?: boolean;
  hideSectionDescriptions?: boolean;
  tableMaxHeightClassName?: string;
}) {
  const { t } = useTranslation("patients");
  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState<"all" | "agendamento" | "vacina" | "retorno" | "estadia">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "agendado" | "pendente">("all");
  const [petFilter, setPetFilter] = useState<string>("all");
  const rows = useMemo(() => [...pendingRows, ...upcomingRows], [pendingRows, upcomingRows]);

  const petFilterOptions = useMemo(() => {
    const byId = new Map<string, string>();
    rows.forEach((row) => {
      if (!row.petId || !row.petName) return;
      if (!byId.has(row.petId)) {
        byId.set(row.petId, row.petName);
      }
    });
    return Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
  }, [rows]);

  const showPetFilter = showPetColumn && petFilterOptions.length > 1;

  const filterRows = useCallback((sourceRows: FutureRow[]) => {
    const normalizedQuery = query.trim().toLowerCase();
    return sourceRows.filter((row) => {
      if (showPetFilter && petFilter !== "all" && row.petId !== petFilter) return false;
      if (kindFilter !== "all" && row.category !== kindFilter) return false;
      if (statusFilter === "agendado" && !row.isScheduled) return false;
      if (statusFilter === "pendente" && row.isScheduled) return false;
      if (!normalizedQuery) return true;
      return [
        row.typeLabel,
        row.detailLabel ?? "",
        row.statusLabel,
        row.dateLabel,
        row.appointmentDateLabel ?? "",
        row.petName ?? "",
        row.professionalName ?? "",
      ].join(" ").toLowerCase().includes(normalizedQuery);
    });
  }, [kindFilter, petFilter, query, showPetFilter, statusFilter]);

  const filteredPendingRows = useMemo(() => filterRows(pendingRows), [filterRows, pendingRows]);
  const filteredUpcomingRows = useMemo(() => filterRows(upcomingRows), [filterRows, upcomingRows]);
  const filteredRowsCount = filteredPendingRows.length + filteredUpcomingRows.length;

  const activeFiltersCount = (query.trim() ? 1 : 0)
    + (kindFilter !== "all" ? 1 : 0)
    + (statusFilter !== "all" ? 1 : 0)
    + (showPetFilter && petFilter !== "all" ? 1 : 0);
  const getCategoryBadgeClass = (category: FutureRow["category"]) => {
    if (category === "vacina") return "bg-gradient-to-r from-emerald-500 to-emerald-600";
    if (category === "retorno") return "bg-gradient-to-r from-amber-500 to-amber-600";
    if (category === "estadia") return "bg-gradient-to-r from-orange-500 to-orange-600";
    return "bg-gradient-to-r from-blue-500 to-blue-600";
  };

  const renderTypeCell = (row: FutureRow) => {
    const typeLabel = row.category === "vacina" && row.detailLabel
      ? row.detailLabel
      : row.typeLabel;

    return (
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "flex !h-8 !w-8 items-center justify-center rounded-full border-2 border-solid border-white shadow-soft-search text-white",
            getCategoryBadgeClass(row.category),
          )}
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{typeLabel}</p>
          {row.category !== "vacina" && row.detailLabel ? <p className="text-xs text-muted-foreground">{row.detailLabel}</p> : null}
        </div>
      </div>
    );
  };

  const renderProfileLink = ({
    id,
    name,
    href,
  }: {
    id?: string | null;
    name?: string | null;
    href: string;
  }) => {
    if (!id || !name) return "—";

    return (
      <Link
        to={href}
        className="font-medium text-primary underline-offset-4 hover:underline"
        onClick={(event) => event.stopPropagation()}
      >
        {name}
      </Link>
    );
  };

  const renderTutorReminderCell = (row: FutureRow) => {
    if (row.category !== "vacina") return "—";

    if (row.tutorReminderStatus === "notified") {
      return row.tutorReminderDateLabel
        ? (
          <div className="leading-tight">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
              {t("futureTab.reminder.sentAtLabel")}
            </p>
            <p>{t("futureTab.reminder.sentAt", { date: row.tutorReminderDateLabel })}</p>
          </div>
        )
        : t("futureTab.reminder.notified");
    }

    if (row.tutorReminderStatus === "window_passed") {
      return (
        <div className="leading-tight">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
            {t("futureTab.reminder.windowPassedLabel")}
          </p>
          {row.tutorReminderDateLabel ? <p>{row.tutorReminderDateLabel}</p> : null}
          <p className="text-xs text-muted-foreground">
            {t("futureTab.reminder.windowPassedWillNotSend")}
          </p>
        </div>
      );
    }

    if (row.tutorReminderStatus === "not_notified") {
      return row.tutorReminderDateLabel
        ? (
          <div className="leading-tight">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
              {t("futureTab.reminder.scheduledForLabel")}
            </p>
            <p>{t("futureTab.reminder.scheduledFor", { date: row.tutorReminderDateLabel })}</p>
          </div>
        )
        : t("futureTab.reminder.notNotified");
    }

    return "—";
  };

  return (
    <>
      <div className={cn("space-y-4")}>
        {showClinicHint ? <p className="text-sm text-muted-foreground">{t("futureTab.clinicHint")}</p> : null}

        {showToolbar ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
            <SearchField
              className="flex-1"
              label={t("futureTab.searchPlaceholder")}
              clearLabel={t("common:clearSearch")}
              placeholder={t("futureTab.searchPlaceholder")}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onClear={() => setQuery("")}
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2 w-full sm:w-auto">
                  <Filter className="h-4 w-4" />
                  {t("futureTab.filters")}
                  {activeFiltersCount > 0 ? <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center">{activeFiltersCount}</Badge> : null}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="start">
                <div className="space-y-4">
                  <div className="font-medium text-sm">{t("futureTab.filters")}</div>
                  <div className="space-y-2">
                    <Label htmlFor="kind-filter">{t("futureTab.type")}</Label>
                    <Select value={kindFilter} onValueChange={(value) => setKindFilter(value as typeof kindFilter)}>
                      <SelectTrigger id="kind-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("futureTab.all")}</SelectItem>
                        <SelectItem value="agendamento">{t("futureTab.appointments")}</SelectItem>
                        <SelectItem value="vacina">{t("futureTab.vaccine")}</SelectItem>
                        <SelectItem value="retorno">{t("futureTab.return")}</SelectItem>
                        <SelectItem value="estadia">{t("historyTab.stay.filterLabel")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status-filter">{t("futureTab.scheduled")}</Label>
                    <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}>
                      <SelectTrigger id="status-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("futureTab.allScheduled")}</SelectItem>
                        <SelectItem value="agendado">{t("futureTab.withAppointment")}</SelectItem>
                        <SelectItem value="pendente">{t("futureTab.withoutAppointment")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {showPetFilter ? (
                    <div className="space-y-2">
                      <Label htmlFor="pet-filter">{t("futureTab.petFilter.label")}</Label>
                      <Select value={petFilter} onValueChange={setPetFilter}>
                        <SelectTrigger id="pet-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("futureTab.petFilter.all")}</SelectItem>
                          {petFilterOptions.map((pet) => (
                            <SelectItem key={pet.id} value={pet.id}>
                              {pet.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}
                  {activeFiltersCount > 0 ? <Button variant="outline" size="sm" className="w-full" onClick={() => { setQuery(""); setKindFilter("all"); setStatusFilter("all"); setPetFilter("all"); }}>{t("futureTab.clearFilters", { count: activeFiltersCount })}</Button> : null}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <p className="text-xs text-muted-foreground">{t("futureTab.items", { count: filteredRowsCount })}</p>
        </div>
        ) : null}

        {isLoading ? (
          <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
        ) : filteredRowsCount === 0 ? (
          <p className="text-sm text-muted-foreground">{upcomingEmptyMessage}</p>
        ) : (
          <div className={cn(compact ? "space-y-4" : "space-y-6")}>
            {renderSection({
              title: t("futureTab.sections.upcoming.title"),
              description: t("futureTab.sections.upcoming.description"),
              rows: filteredUpcomingRows,
              emptyMessage: upcomingEmptyMessage,
            })}
            {renderSection({
              title: t("futureTab.sections.pending.title"),
              description: t("futureTab.sections.pending.description"),
              rows: filteredPendingRows,
              emptyMessage: pendingEmptyMessage,
              tone: "pending",
            })}
          </div>
        )}
      </div>
    </>
  );

  function renderSection({
    title,
    description,
    rows: sectionRows,
    emptyMessage,
    tone = "default",
  }: {
    title: string;
    description: string;
    rows: FutureRow[];
    emptyMessage: string;
    tone?: "default" | "pending";
  }) {
    const sectionToneClasses = tone === "pending"
      ? "border-primary/20"
      : "border-border/80";
    const headerToneClasses = tone === "pending"
      ? "bg-secondary/30"
      : "bg-muted/20";
    const badgeToneClasses = tone === "pending"
      ? "border-secondary/80 bg-secondary/80 text-primary"
      : "border-border/60 bg-background text-foreground";

    return (
      <section className={["overflow-hidden rounded-xl border bg-white", sectionToneClasses].join(" ")}>
        <div className={cn("border-b px-4 sm:px-5", compact ? "py-3" : "py-4", headerToneClasses, sectionToneClasses)}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <h3 className="text-base font-semibold text-foreground">{title}</h3>
              {!hideSectionDescriptions ? (
                <p className="text-sm text-muted-foreground">{description}</p>
              ) : null}
            </div>
            <Badge variant="outline" className={badgeToneClasses}>
              {t("futureTab.items", { count: sectionRows.length })}
            </Badge>
          </div>
        </div>

        {sectionRows.length === 0 ? (
          <div className="px-4 py-6 sm:px-5">
            <p className="text-sm text-muted-foreground">{emptyMessage}</p>
          </div>
        ) : (
          <div className={cn("w-full overflow-auto", tableMaxHeightClassName)}>
            <div className="min-w-max">
              <Table className="min-w-max">
                <TableHeader className={cn(tone === "pending" ? "[&_tr]:border-secondary" : undefined)}>
                  <TableRow className="hover:bg-transparent">
                      <TableHead>{t("futureTab.columns.type")}</TableHead>
                      {showPetColumn ? <TableHead>{t("futureTab.columns.patient")}</TableHead> : null}
                      <TableHead>{t("futureTab.columns.expectedDate")}</TableHead>
                      <TableHead>{t("futureTab.columns.professional")}</TableHead>
                      <TableHead>{t("futureTab.columns.appointment")}</TableHead>
                      <TableHead>{t("futureTab.columns.tutorReminder")}</TableHead>
                      <TableHead className="text-right">{t("futureTab.columns.estimatedValue")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className={cn(tone === "pending" ? "[&_tr]:border-secondary" : undefined)}>
                    {sectionRows.map((row) => (
                      <TableRow
                        key={row.key}
                        className={isRowClickable(row) ? cn("cursor-pointer transition-colors", tone === "pending" ? "hover:bg-secondary/40" : "hover:bg-muted/40") : undefined}
                        onClick={getRowClickHandler(row)}
                      >
                        <TableCell>{renderTypeCell(row)}</TableCell>
                        {showPetColumn ? (
                          <TableCell>
                            {renderProfileLink({
                              id: row.petId,
                              name: row.petName,
                              href: `/patients/pets/${row.petId}`,
                            })}
                          </TableCell>
                        ) : null}
                        <TableCell className="text-sm text-muted-foreground">{row.dateLabel}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{row.professionalName ?? "—"}</TableCell>
                        <TableCell><AppointmentStatusBadge statusKey={row.statusKey} statusLabel={row.statusLabel} resolvedApplied={row.resolutionStatus === "resolved_applied"} /></TableCell>
                        <TableCell className="text-sm text-muted-foreground">{renderTutorReminderCell(row)}</TableCell>
                        <TableCell className="text-right font-medium">{row.estimatedAmount != null ? formatCurrency(row.estimatedAmount) : "—"}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </section>
    );
  }

  function isRowClickable(row: FutureRow) {
    return Boolean(row.editableVaccination || row.editablePlan || row.appointmentId || row.category === "estadia");
  }

  function getRowClickHandler(row: FutureRow) {
    if (row.editableVaccination) {
      return () => onVaccinationSelect?.(row.editableVaccination!, row);
    }

    if (row.editablePlan) {
      return () => onPlanSelect?.(row.editablePlan!, row);
    }

    if (row.appointmentId) {
      return () => onAppointmentSelect?.(row);
    }

    if (row.category === "estadia" && row.petId) {
      return () => window.location.assign(`/patients/pets/${row.petId}`);
    }

    return undefined;
  }
}
